import { writable, derived, get } from 'svelte/store';
import { browser } from '$app/environment';
import { CategoriesAPI } from '$lib/api/categories';
import { extractDisplayOrder } from '$lib/features/categories/components/CategoryTreePanel/CategoryTreePanel.logic';
import type { CategoryRead } from '$lib/api/generated/schemas/categoryRead';
import { getTrackersApiTrackersGet } from '$lib/api/generated/api';
import { favoritesApi } from '$lib/api/favoritesApi';
import type {
	DataTreeState,
	DataTreeActions,
	CategoryNode,
	TrackerNode,
	TreeNode,
	SidebarCategoryItem,
	SidebarTrackerItem,
	SidebarFavoriteItem
} from './dataTreeStore.types';
import {
	DEFAULT_SETTINGS,
	loadPersistedState,
	savePersistedState,
	clearPersistedState,
	buildCategoryTree,
	buildRenderTree,
	transformTrackerToNode,
	convertToSidebarCategory,
	convertToSidebarTracker,
	getCategoryPath,
	getCategoryParents,
	searchInTree,
	logDataTreeState
} from './dataTreeStore.utils';

// ===== CORE STORE CREATION =====

function createDataTreeStore() {
	const categoryTrackersPromises = new Map<string, Promise<boolean>>();
	let sessionGeneration = 0;
	let trackerLoadGeneration = 0;
	let latestAppliedTrackerLoadGeneration = 0;
	// Load persisted state
	const persistedState = loadPersistedState();

	// Initialize store state
	const initialState: DataTreeState = {
		categories: new Map(),
		trackers: new Map(),
		tree: [],
		expandedCategories: new Set(persistedState.expandedCategories || []),
		favorites: new Set(persistedState.favorites || []),
		favoriteCategories: new Set(persistedState.favoriteCategories || []),
		isLoading: false,
		isCategoriesLoading: false,
		isTrackersLoading: false,
		error: null,
		lastUpdated: null,
		lastCategoriesUpdate: null,
		lastTrackersUpdate: null,
		categoryDisplayOrder: [],
		autoExpandOnNavigation:
			persistedState.settings?.autoExpandOnNavigation ?? DEFAULT_SETTINGS.autoExpandOnNavigation,
		maxTreeDepth: persistedState.settings?.maxTreeDepth ?? DEFAULT_SETTINGS.maxTreeDepth
	};

	const { subscribe, set, update } = writable<DataTreeState>(initialState);

	// Store reference to loadCategoryTrackersIfNeeded for safe access in actions
	let loadCategoryTrackersIfNeededRef: (categoryId: string) => Promise<boolean>;

	async function loadCategoryTrackersIfNeeded(categoryId: string): Promise<boolean> {
		const requestSessionGeneration = sessionGeneration;
		const state = get({ subscribe });
		const hasTrackers = Array.from(state.trackers.values()).some(
			(tracker) => tracker.category_id === categoryId
		);
		if (hasTrackers) {
			return false;
		}

		const existingPromise = categoryTrackersPromises.get(categoryId);
		if (existingPromise) {
			return existingPromise;
		}

		let loadPromise!: Promise<boolean>;
		loadPromise = (async () => {
			try {
				const trackers = await fetchTrackersForCategory(categoryId);
				if (requestSessionGeneration !== sessionGeneration) return false;
				let loaded = false;
				update((state) => {
					const newTrackers = new Map(state.trackers);
					trackers.forEach((tracker) => {
						newTrackers.set(tracker.id, tracker);
					});
					loaded = trackers.length > 0;
					return {
						...state,
						trackers: newTrackers
					};
				});
				return loaded;
			} catch (error) {
				if (requestSessionGeneration !== sessionGeneration) return false;
				console.warn(`Failed to load trackers for category ${categoryId}:`, error);
				return false;
			} finally {
				if (categoryTrackersPromises.get(categoryId) === loadPromise) {
					categoryTrackersPromises.delete(categoryId);
				}
			}
		})();

		categoryTrackersPromises.set(categoryId, loadPromise);
		return loadPromise;
	}

	// Store reference for safe access
	loadCategoryTrackersIfNeededRef = loadCategoryTrackersIfNeeded;

	// ===== PRIVATE HELPER FUNCTIONS =====

	async function fetchCategories(): Promise<CategoryRead[]> {
		const response = await CategoriesAPI.getCategories();
		return response.categories;
	}

	async function fetchTrackersForCategory(categoryId: string): Promise<TrackerNode[]> {
		const response = await getTrackersApiTrackersGet({
			category_id: categoryId,
			sort_by: 'created_at',
			sort_order: 'desc'
		});

		const currentState = get({ subscribe });
		return response.items.map((tracker) =>
			transformTrackerToNode(tracker, currentState.favorites.has(tracker.id))
		);
	}

	async function fetchAllTrackers(): Promise<TrackerNode[]> {
		const response = await getTrackersApiTrackersGet({
			sort_by: 'created_at',
			sort_order: 'desc'
		});

		const currentState = get({ subscribe });
		return response.items.map((tracker) =>
			transformTrackerToNode(tracker, currentState.favorites.has(tracker.id))
		);
	}

	function rebuildTree(): void {
		update((state) => {
			// Build category tree
			const flatCategories = Array.from(state.categories.values());
			const categoryTrackers = new Map<string, TrackerNode[]>();
			const uncategorizedTrackers: TrackerNode[] = [];

			// Group trackers by category
			state.trackers.forEach((tracker) => {
				if (tracker.category_id) {
					const existing = categoryTrackers.get(tracker.category_id) || [];
					existing.push(tracker);
					categoryTrackers.set(tracker.category_id, existing);
				} else {
					// Collect trackers without category
					uncategorizedTrackers.push(tracker);
				}
			});

			const categoryTree = buildCategoryTree(
				flatCategories,
				state.expandedCategories,
				categoryTrackers,
				state.favoriteCategories
			);

			// Build render tree with uncategorized trackers
			const renderTree = buildRenderTree(categoryTree, uncategorizedTrackers);

			// Update categories with trackers
			categoryTree.forEach((categoryNode) => {
				state.categories.set(categoryNode.id, categoryNode);
			});

			return {
				...state,
				tree: renderTree
			};
		});
	}

	function persistCurrentState(): void {
		const state = get({ subscribe });
		savePersistedState({
			expandedCategories: [...state.expandedCategories],
			favorites: [...state.favorites],
			favoriteCategories: [...state.favoriteCategories],
			settings: {
				autoExpandOnNavigation: state.autoExpandOnNavigation,
				maxTreeDepth: state.maxTreeDepth
			}
		});
	}

	// ===== PUBLIC ACTIONS =====

	const actions: DataTreeActions = {
		// Core loading
		async loadAll() {
			const requestSessionGeneration = sessionGeneration;
			update((state) => ({ ...state, isLoading: true, error: null }));

			try {
				await Promise.all([
					actions.loadCategories(),
					actions.loadFavorites()
					// Don't load all trackers initially - load on demand
				]);
				if (requestSessionGeneration !== sessionGeneration) return;

				update((state) => ({
					...state,
					isLoading: false,
					lastUpdated: new Date()
				}));
			} catch (error) {
				if (requestSessionGeneration !== sessionGeneration) return;
				const errorMessage = error instanceof Error ? error.message : 'Failed to load data';
				update((state) => ({
					...state,
					isLoading: false,
					error: errorMessage
				}));
				throw error;
			}
		},

		async loadCategories() {
			const requestSessionGeneration = sessionGeneration;
			update((state) => ({ ...state, isCategoriesLoading: true }));

			try {
				const categories = await fetchCategories();
				if (requestSessionGeneration !== sessionGeneration) return;

				update((state) => {
					// Update categories map
					const newCategories = new Map<string, CategoryNode>();
					categories.forEach((cat) => {
						const existing = state.categories.get(cat.id);
						const categoryNode: CategoryNode = {
							...cat,
							level: existing?.level || 0,
							isExpanded: existing?.isExpanded || state.expandedCategories.has(cat.id),
							hasChildren: existing?.hasChildren || (cat.active_tracker_count || 0) > 0,
							children: existing?.children || [],
							trackers: existing?.trackers || [],
							parentPath: existing?.parentPath || [],
							href: `/app/journal?category=${cat.id}`,
							trackerCount: existing?.trackerCount || cat.active_tracker_count || 0,
							isFavorite: existing?.isFavorite || state.favoriteCategories.has(cat.id)
						};
						newCategories.set(cat.id, categoryNode);
					});

					return {
						...state,
						categories: newCategories,
						isCategoriesLoading: false,
						lastCategoriesUpdate: new Date()
					};
				});

				// Ensure trackers for favorite categories are loaded
				const favoriteCategoryIds = Array.from(get({ subscribe }).favoriteCategories);
				if (favoriteCategoryIds.length > 0) {
					await Promise.all(favoriteCategoryIds.map((id) => loadCategoryTrackersIfNeeded(id)));
				}
				if (requestSessionGeneration !== sessionGeneration) return;

				// Rebuild tree after categories are loaded
				rebuildTree();

				// Load display order for sidebar sorting
				try {
					const orderedTree = await CategoriesAPI.getCategoryTreeOrdered();
					if (requestSessionGeneration !== sessionGeneration) return;
					const displayOrder = extractDisplayOrder(orderedTree);
					update((state) => ({ ...state, categoryDisplayOrder: displayOrder }));
				} catch {
					console.warn('[dataTreeStore] Failed to load category display order');
				}
			} catch (error) {
				if (requestSessionGeneration !== sessionGeneration) return;
				const errorMessage = error instanceof Error ? error.message : 'Failed to load categories';
				update((state) => ({
					...state,
					isCategoriesLoading: false,
					error: errorMessage
				}));
				throw error;
			}
		},

		async loadTrackers() {
			const requestGeneration = ++trackerLoadGeneration;
			update((state) => ({ ...state, isTrackersLoading: true }));

			try {
				const trackers = await fetchAllTrackers();
				if (requestGeneration < latestAppliedTrackerLoadGeneration) return;
				latestAppliedTrackerLoadGeneration = requestGeneration;

				update((state) => {
					const newTrackers = new Map<string, TrackerNode>();
					trackers.forEach((tracker) => {
						newTrackers.set(tracker.id, tracker);
					});

					return {
						...state,
						trackers: newTrackers,
						isTrackersLoading:
							requestGeneration === trackerLoadGeneration ? false : state.isTrackersLoading,
						lastTrackersUpdate: new Date()
					};
				});

				// Rebuild tree after trackers are loaded
				rebuildTree();
			} catch (error) {
				if (requestGeneration === trackerLoadGeneration) {
					const errorMessage = error instanceof Error ? error.message : 'Failed to load trackers';
					update((state) => ({
						...state,
						isTrackersLoading: false,
						error: errorMessage
					}));
				}
				throw error;
			}
		},

		async refresh() {
			return actions.loadAll();
		},

		// Category actions
		async toggleCategory(categoryId: string) {
			const requestSessionGeneration = sessionGeneration;
			const currentState = get({ subscribe });
			const isCurrentlyExpanded = currentState.expandedCategories.has(categoryId);

			if (isCurrentlyExpanded) {
				// Collapse category
				update((state) => {
					const newExpanded = new Set(state.expandedCategories);
					newExpanded.delete(categoryId);

					return {
						...state,
						expandedCategories: newExpanded
					};
				});
			} else {
				// Expand category - first update UI, then load trackers
				update((state) => {
					const newExpanded = new Set(state.expandedCategories);
					newExpanded.add(categoryId);

					return {
						...state,
						expandedCategories: newExpanded
					};
				});

				// Load trackers for this category
				await loadCategoryTrackersIfNeeded(categoryId);
				if (requestSessionGeneration !== sessionGeneration) return;
			}

			// Rebuild tree and persist state
			rebuildTree();
			persistCurrentState();
		},

		async ensureCategoryTrackersLoaded(categoryId: string) {
			const requestSessionGeneration = sessionGeneration;
			const loaded = await loadCategoryTrackersIfNeeded(categoryId);
			if (requestSessionGeneration !== sessionGeneration) return;
			if (loaded) {
				rebuildTree();
				persistCurrentState();
			}
		},

		expandCategory(categoryId: string, expandParents: boolean = false) {
			update((state) => {
				const newExpanded = new Set(state.expandedCategories);
				newExpanded.add(categoryId);

				if (expandParents) {
					const parents = getCategoryParents(categoryId, state.categories);
					parents.forEach((parent) => newExpanded.add(parent.id));
				}

				return {
					...state,
					expandedCategories: newExpanded
				};
			});

			rebuildTree();
			persistCurrentState();
		},

		collapseCategory(categoryId: string, collapseChildren: boolean = false) {
			update((state) => {
				const newExpanded = new Set(state.expandedCategories);
				newExpanded.delete(categoryId);

				if (collapseChildren) {
					// Find and collapse all descendant categories
					const findDescendants = (parentId: string): string[] => {
						const descendants: string[] = [];
						state.categories.forEach((cat) => {
							if (cat.parent_id === parentId) {
								descendants.push(cat.id);
								descendants.push(...findDescendants(cat.id));
							}
						});
						return descendants;
					};

					const descendants = findDescendants(categoryId);
					descendants.forEach((id) => newExpanded.delete(id));
				}

				return {
					...state,
					expandedCategories: newExpanded
				};
			});

			rebuildTree();
			persistCurrentState();
		},

		expandForRoute(routePath: string) {
			const categoryMatch = routePath.match(/^\/category\/([^\/]+)/);
			if (!categoryMatch) return;

			const categoryId = categoryMatch[1];
			const state = get({ subscribe });
			const category = state.categories.get(categoryId);

			if (category) {
				actions.expandCategory(categoryId, true);
			}
		},

		collapseAll() {
			update((state) => ({
				...state,
				expandedCategories: new Set<string>()
			}));

			rebuildTree();
			persistCurrentState();
		},

		expandAll(maxDepth: number = 2) {
			update((state) => {
				const newExpanded = new Set<string>();

				state.categories.forEach((category) => {
					const parents = getCategoryParents(category.id, state.categories);
					if (parents.length < maxDepth) {
						newExpanded.add(category.id);
					}
				});

				return {
					...state,
					expandedCategories: newExpanded
				};
			});

			rebuildTree();
			persistCurrentState();
		},

		// Tracker actions
		addTracker(tracker: TrackerNode) {
			update((state) => {
				const newTrackers = new Map(state.trackers);
				newTrackers.set(tracker.id, tracker);

				return {
					...state,
					trackers: newTrackers
				};
			});

			rebuildTree();
		},

		updateTracker(trackerId: string, updates: Partial<TrackerNode>) {
			update((state) => {
				const existing = state.trackers.get(trackerId);
				if (!existing) return state;

				const updated = { ...existing, ...updates };
				const newTrackers = new Map(state.trackers);
				newTrackers.set(trackerId, updated);

				return {
					...state,
					trackers: newTrackers
				};
			});

			rebuildTree();
		},

		removeTracker(trackerId: string) {
			update((state) => {
				const newTrackers = new Map(state.trackers);
				newTrackers.delete(trackerId);

				const newFavorites = new Set(state.favorites);
				newFavorites.delete(trackerId);

				return {
					...state,
					trackers: newTrackers,
					favorites: newFavorites
				};
			});

			rebuildTree();
			persistCurrentState();
		},

		moveTracker(trackerId: string, newCategoryId: string) {
			actions.updateTracker(trackerId, { category_id: newCategoryId });
		},

		// Favorites actions
		async toggleFavorite(trackerId: string) {
			const state = get({ subscribe });
			if (state.favorites.has(trackerId)) {
				await actions.removeFavorite(trackerId);
			} else {
				await actions.addFavorite(trackerId);
			}
		},

		async addFavorite(trackerId: string) {
			const requestSessionGeneration = sessionGeneration;
			// Save previous state for rollback
			const currentState = get({ subscribe });
			const previousState: DataTreeState = {
				...currentState,
				favorites: new Set(currentState.favorites),
				favoriteCategories: new Set(currentState.favoriteCategories),
				expandedCategories: new Set(currentState.expandedCategories),
				trackers: new Map(currentState.trackers),
				categories: new Map(currentState.categories),
				tree: [...currentState.tree]
			};

			// Optimistic update
			update((state) => {
				const newFavorites = new Set(state.favorites);
				newFavorites.add(trackerId);

				// Update tracker isFavorite flag
				const tracker = state.trackers.get(trackerId);
				if (tracker) {
					const newTrackers = new Map(state.trackers);
					newTrackers.set(trackerId, { ...tracker, isFavorite: true });

					return {
						...state,
						favorites: newFavorites,
						trackers: newTrackers
					};
				}

				return {
					...state,
					favorites: newFavorites
				};
			});

			persistCurrentState();
			rebuildTree();

			// Sync with backend
			try {
				await favoritesApi.addToFavorites('tracker', trackerId);
			} catch (error) {
				if (requestSessionGeneration !== sessionGeneration) return;
				// Rollback on error
				set(previousState);
				persistCurrentState();
				rebuildTree();
				console.error('Failed to add tracker to favorites:', error);
				throw error;
			}
		},

		async toggleCategoryFavorite(categoryId: string) {
			const state = get({ subscribe });
			if (state.favoriteCategories.has(categoryId)) {
				await actions.removeCategoryFavorite(categoryId);
			} else {
				await actions.addCategoryFavorite(categoryId);
			}
		},

		async addCategoryFavorite(categoryId: string) {
			const requestSessionGeneration = sessionGeneration;
			// Save previous state for rollback
			const currentState = get({ subscribe });
			const previousState: DataTreeState = {
				...currentState,
				favorites: new Set(currentState.favorites),
				favoriteCategories: new Set(currentState.favoriteCategories),
				expandedCategories: new Set(currentState.expandedCategories),
				trackers: new Map(currentState.trackers),
				categories: new Map(currentState.categories),
				tree: [...currentState.tree]
			};

			// Optimistic update
			update((state) => {
				const newFavoriteCategories = new Set(state.favoriteCategories);
				newFavoriteCategories.add(categoryId);

				const newCategories = new Map(state.categories);
				const category = newCategories.get(categoryId);
				if (category) {
					newCategories.set(categoryId, { ...category, isFavorite: true });
				}

				return {
					...state,
					favoriteCategories: newFavoriteCategories,
					categories: newCategories
				};
			});

			await loadCategoryTrackersIfNeeded(categoryId);
			if (requestSessionGeneration !== sessionGeneration) return;

			rebuildTree();
			persistCurrentState();

			// Sync with backend
			try {
				await favoritesApi.addToFavorites('category', categoryId);
			} catch (error) {
				if (requestSessionGeneration !== sessionGeneration) return;
				// Rollback on error
				set(previousState);
				persistCurrentState();
				rebuildTree();
				console.error('Failed to add category to favorites:', error);
				throw error;
			}
		},

		async removeCategoryFavorite(categoryId: string) {
			const requestSessionGeneration = sessionGeneration;
			// Save previous state for rollback
			const currentState = get({ subscribe });
			const previousState: DataTreeState = {
				...currentState,
				favorites: new Set(currentState.favorites),
				favoriteCategories: new Set(currentState.favoriteCategories),
				expandedCategories: new Set(currentState.expandedCategories),
				trackers: new Map(currentState.trackers),
				categories: new Map(currentState.categories),
				tree: [...currentState.tree]
			};

			// Optimistic update
			update((state) => {
				const newFavoriteCategories = new Set(state.favoriteCategories);
				newFavoriteCategories.delete(categoryId);

				const newCategories = new Map(state.categories);
				const category = newCategories.get(categoryId);
				if (category) {
					newCategories.set(categoryId, { ...category, isFavorite: false });
				}

				return {
					...state,
					favoriteCategories: newFavoriteCategories,
					categories: newCategories
				};
			});

			persistCurrentState();
			rebuildTree();

			// Sync with backend
			try {
				await favoritesApi.removeFromFavorites('category', categoryId);
			} catch (error) {
				if (requestSessionGeneration !== sessionGeneration) return;
				// Rollback on error
				set(previousState);
				persistCurrentState();
				rebuildTree();
				console.error('Failed to remove category from favorites:', error);
				throw error;
			}
		},

		async removeFavorite(trackerId: string) {
			const requestSessionGeneration = sessionGeneration;
			// Save previous state for rollback
			const currentState = get({ subscribe });
			const previousState: DataTreeState = {
				...currentState,
				favorites: new Set(currentState.favorites),
				favoriteCategories: new Set(currentState.favoriteCategories),
				expandedCategories: new Set(currentState.expandedCategories),
				trackers: new Map(currentState.trackers),
				categories: new Map(currentState.categories),
				tree: [...currentState.tree]
			};

			// Optimistic update
			update((state) => {
				const newFavorites = new Set(state.favorites);
				newFavorites.delete(trackerId);

				// Update tracker isFavorite flag
				const tracker = state.trackers.get(trackerId);
				if (tracker) {
					const newTrackers = new Map(state.trackers);
					newTrackers.set(trackerId, { ...tracker, isFavorite: false });

					return {
						...state,
						favorites: newFavorites,
						trackers: newTrackers
					};
				}

				return {
					...state,
					favorites: newFavorites
				};
			});

			persistCurrentState();
			rebuildTree();

			// Sync with backend
			try {
				await favoritesApi.removeFromFavorites('tracker', trackerId);
			} catch (error) {
				if (requestSessionGeneration !== sessionGeneration) return;
				// Rollback on error
				set(previousState);
				persistCurrentState();
				rebuildTree();
				console.error('Failed to remove tracker from favorites:', error);
				throw error;
			}
		},

		// Load favorites from API and sync with store
		async loadFavorites() {
			const requestSessionGeneration = sessionGeneration;
			try {
				const response = await favoritesApi.getFavorites();
				if (requestSessionGeneration !== sessionGeneration) return;

				const categoriesToLoad = new Set<string>();

				update((state) => {
					const newFavorites = new Set<string>();
					const newFavoriteCategories = new Set<string>();

					// Process favorites from API
					for (const favorite of response.favorites || []) {
						if (favorite.type === 'tracker') {
							newFavorites.add(favorite.id);
							// Extract category_id from item_data for tracker favorites
							if (
								favorite.item_data?.category_id &&
								typeof favorite.item_data.category_id === 'string'
							) {
								categoriesToLoad.add(favorite.item_data.category_id);
							}
						} else if (favorite.type === 'category') {
							newFavoriteCategories.add(favorite.id);
							categoriesToLoad.add(favorite.id);
						}
					}

					// Update trackers and categories isFavorite flags
					const newTrackers = new Map(state.trackers);
					for (const trackerId of newFavorites) {
						const tracker = newTrackers.get(trackerId);
						if (tracker) {
							newTrackers.set(trackerId, { ...tracker, isFavorite: true });
						}
					}
					// Remove isFavorite flag from trackers not in favorites
					for (const [trackerId, tracker] of newTrackers.entries()) {
						if (!newFavorites.has(trackerId) && tracker.isFavorite) {
							newTrackers.set(trackerId, { ...tracker, isFavorite: false });
						}
					}

					const newCategories = new Map(state.categories);
					for (const categoryId of newFavoriteCategories) {
						const category = newCategories.get(categoryId);
						if (category) {
							newCategories.set(categoryId, { ...category, isFavorite: true });
						}
					}
					// Remove isFavorite flag from categories not in favorites
					for (const [categoryId, category] of newCategories.entries()) {
						if (!newFavoriteCategories.has(categoryId) && category.isFavorite) {
							newCategories.set(categoryId, { ...category, isFavorite: false });
						}
					}

					return {
						...state,
						favorites: newFavorites,
						favoriteCategories: newFavoriteCategories,
						trackers: newTrackers,
						categories: newCategories
					};
				});

				// Load trackers for favorite categories and categories of favorite trackers
				// This ensures favorite trackers are visible even if their category wasn't expanded
				if (categoriesToLoad.size > 0) {
					await Promise.all(
						Array.from(categoriesToLoad).map((categoryId) =>
							loadCategoryTrackersIfNeededRef(categoryId)
						)
					);
				}
				if (requestSessionGeneration !== sessionGeneration) return;

				persistCurrentState();
				rebuildTree();
			} catch (error) {
				if (requestSessionGeneration !== sessionGeneration) return;
				console.error('Failed to load favorites from API:', error);
				// Don't throw - allow app to continue with local state
			}
		},

		// Navigation and path
		getCategoryPath(categoryId: string) {
			const state = get({ subscribe });
			return getCategoryPath(categoryId, state.categories);
		},

		getTrackersByCategory(categoryId: string) {
			const state = get({ subscribe });
			const trackers: TrackerNode[] = [];

			state.trackers.forEach((tracker) => {
				if (tracker.category_id === categoryId) {
					trackers.push(tracker);
				}
			});

			return trackers.sort((a, b) => a.name.localeCompare(b.name));
		},

		getFlatTrackersList() {
			const state = get({ subscribe });
			return Array.from(state.trackers.values())
				.filter((tracker) => !tracker.archived_at && !tracker.deleted_at)
				.sort((a, b) => a.name.localeCompare(b.name));
		},

		// Settings
		updateSettings(
			settings: Partial<Pick<DataTreeState, 'autoExpandOnNavigation' | 'maxTreeDepth'>>
		) {
			update((state) => ({
				...state,
				...settings
			}));

			persistCurrentState();
		},

		// Utility
		clearError() {
			update((state) => ({ ...state, error: null }));
		},

		reset() {
			const currentState = get({ subscribe });
			sessionGeneration += 1;
			latestAppliedTrackerLoadGeneration = ++trackerLoadGeneration;
			categoryTrackersPromises.clear();
			clearPersistedState();
			set({
				categories: new Map(),
				trackers: new Map(),
				tree: [],
				expandedCategories: new Set(),
				favorites: new Set(),
				favoriteCategories: new Set(),
				isLoading: false,
				isCategoriesLoading: false,
				isTrackersLoading: false,
				error: null,
				lastUpdated: null,
				lastCategoriesUpdate: null,
				lastTrackersUpdate: null,
				autoExpandOnNavigation: currentState.autoExpandOnNavigation,
				maxTreeDepth: currentState.maxTreeDepth,
				categoryDisplayOrder: []
			});
		}
	};

	return {
		subscribe,
		...actions
	};
}

// ===== STORE INSTANCE AND DERIVED STORES =====

export const dataTreeStore = createDataTreeStore();

// Derived stores for common use cases
export const isDataTreeLoading = derived(
	dataTreeStore,
	($store) => $store.isLoading || $store.isCategoriesLoading || $store.isTrackersLoading
);

export const dataTreeError = derived(dataTreeStore, ($store) => $store.error);

export const renderTree = derived(dataTreeStore, ($store) => $store.tree);

export const flatCategories = derived(dataTreeStore, ($store) =>
	Array.from($store.categories.values())
);

export const flatTrackers = derived(dataTreeStore, ($store) =>
	Array.from($store.trackers.values()).filter(
		(tracker) => !tracker.archived_at && !tracker.deleted_at
	)
);

export const favoriteTrackers = derived(dataTreeStore, ($store) =>
	Array.from($store.trackers.values()).filter(
		(tracker) => $store.favorites.has(tracker.id) && !tracker.archived_at && !tracker.deleted_at
	)
);

export const expandedCategories = derived(dataTreeStore, ($store) => $store.expandedCategories);

// Sidebar compatibility derived stores
export const sidebarCategories = derived(dataTreeStore, ($store) => {
	// Sort helper: use display order when available, fall back to alphabetical
	const orderMap = new Map($store.categoryDisplayOrder.map((id: string, i: number) => [id, i]));
	function sortByOrder<T extends { id: string; name: string }>(items: T[]): T[] {
		if (orderMap.size === 0) return [...items].sort((a, b) => a.name.localeCompare(b.name));
		return [...items].sort((a, b) => {
			const aIdx = orderMap.get(a.id) ?? Infinity;
			const bIdx = orderMap.get(b.id) ?? Infinity;
			if (aIdx !== bIdx) return aIdx - bIdx;
			return a.name.localeCompare(b.name);
		});
	}

	// Sort children recursively after conversion
	function sortCategoryTree(items: SidebarCategoryItem[]): SidebarCategoryItem[] {
		return sortByOrder(items).map((cat) => ({
			...cat,
			children: cat.children ? sortCategoryTree(cat.children) : undefined
		}));
	}

	const rootCategories = Array.from($store.categories.values()).filter((cat) => !cat.parent_id);

	const categoriesWithTrackers = sortCategoryTree(rootCategories.map(convertToSidebarCategory));

	// Add uncategorized trackers if any exist
	const uncategorizedTrackers = Array.from($store.trackers.values()).filter(
		(tracker) => !tracker.category_id && !tracker.archived_at && !tracker.deleted_at
	);

	if (uncategorizedTrackers.length > 0) {
		// Check if uncategorized is expanded (defaults to collapsed)
		const isUncategorizedExpanded = $store.expandedCategories.has('uncategorized');

		const uncategorizedCategory = {
			id: 'uncategorized',
			name: 'Uncategorized',
			color: '#6B7280',
			count: uncategorizedTrackers.length,
			level: 0,
			parentId: undefined,
			isExpanded: isUncategorizedExpanded,
			hasChildren: true,
			children: [],
			trackers: isUncategorizedExpanded ? uncategorizedTrackers.map(convertToSidebarTracker) : [],
			href: '/uncategorized'
		};

		categoriesWithTrackers.push(uncategorizedCategory);
	}

	return categoriesWithTrackers;
});

export const sidebarFavorites = derived(dataTreeStore, ($store): SidebarFavoriteItem[] => {
	const trackerFavorites = Array.from($store.trackers.values())
		.filter((tracker) => $store.favorites.has(tracker.id))
		.map((tracker) => ({
			type: 'tracker' as const,
			id: tracker.id,
			name: tracker.name,
			color: tracker.color,
			icon: tracker.icon,
			lastValue: tracker.lastValue,
			unit: tracker.unit,
			lastEntryDate: tracker.last_entry_date,
			href: `/app/trackers/${tracker.id}`
		}))
		.sort((a, b) => a.name.localeCompare(b.name));

	const convertCategoryToSidebarFavorite = (category: CategoryNode): SidebarFavoriteItem => {
		const directTrackers = category.trackers.map(convertToSidebarTracker);
		const subcategories = category.children
			.filter((child) => $store.favoriteCategories.has(child.id))
			.map((child) => convertCategoryToSidebarFavorite(child));

		// Calculate total tracker count including subcategories
		const subcategoryTrackerCount = subcategories.reduce(
			(sum, sub) => sum + (sub.trackerCount || 0),
			0
		);
		const totalTrackerCount = directTrackers.length + subcategoryTrackerCount;

		return {
			type: 'category' as const,
			id: category.id,
			name: category.name,
			color: category.color || undefined,
			icon: category.icon || undefined,
			trackerCount: totalTrackerCount,
			href: category.href,
			trackers: directTrackers,
			subcategories: subcategories.length > 0 ? subcategories : undefined
		};
	};

	const categoryFavorites = Array.from($store.favoriteCategories)
		.map((categoryId) => $store.categories.get(categoryId))
		.filter((category): category is CategoryNode => Boolean(category))
		// Only include top-level categories (those without favorite parents)
		.filter((category) => {
			const parent =
				category.parentPath.length > 0
					? $store.categories.get(category.parentPath[category.parentPath.length - 1])
					: null;
			return !parent || !$store.favoriteCategories.has(parent.id);
		})
		.map((category) => convertCategoryToSidebarFavorite(category))
		.sort((a, b) => a.name.localeCompare(b.name));

	return [...categoryFavorites, ...trackerFavorites];
});

// Actions export for easy importing
export const dataTreeActions = {
	loadAll: dataTreeStore.loadAll,
	loadCategories: dataTreeStore.loadCategories,
	loadTrackers: dataTreeStore.loadTrackers,
	loadFavorites: dataTreeStore.loadFavorites,
	refresh: dataTreeStore.refresh,
	toggleCategory: dataTreeStore.toggleCategory,
	expandCategory: dataTreeStore.expandCategory,
	collapseCategory: dataTreeStore.collapseCategory,
	expandForRoute: dataTreeStore.expandForRoute,
	collapseAll: dataTreeStore.collapseAll,
	expandAll: dataTreeStore.expandAll,
	addTracker: dataTreeStore.addTracker,
	updateTracker: dataTreeStore.updateTracker,
	removeTracker: dataTreeStore.removeTracker,
	moveTracker: dataTreeStore.moveTracker,
	toggleFavorite: dataTreeStore.toggleFavorite,
	addFavorite: dataTreeStore.addFavorite,
	removeFavorite: dataTreeStore.removeFavorite,
	toggleCategoryFavorite: dataTreeStore.toggleCategoryFavorite,
	addCategoryFavorite: dataTreeStore.addCategoryFavorite,
	removeCategoryFavorite: dataTreeStore.removeCategoryFavorite,
	getCategoryPath: dataTreeStore.getCategoryPath,
	getTrackersByCategory: dataTreeStore.getTrackersByCategory,
	ensureCategoryTrackersLoaded: dataTreeStore.ensureCategoryTrackersLoaded,
	getFlatTrackersList: dataTreeStore.getFlatTrackersList,
	updateSettings: dataTreeStore.updateSettings,
	clearError: dataTreeStore.clearError,
	reset: dataTreeStore.reset
};

// Export types for easy importing
export type {
	DataTreeState,
	CategoryNode,
	TrackerNode,
	TreeNode,
	SidebarCategoryItem,
	SidebarTrackerItem,
	SidebarFavoriteItem
} from './dataTreeStore.types';
